import { NavLink } from 'react-router-dom';
import { cn } from '@kidshome/ui';

/**
 * Bottom tab bar (mockup 02). Token-styled, 44px+ targets, `aria-current` on the active tab via
 * NavLink. Items are passed in by the persona shell (kid: Home · Habits · Learn · Rewards · Profile;
 * family: Home · Calendar · Rewards · Inbox/Family · History — both capped at five). Tabs share the
 * row width (`flex-1` + `min-w-0`) and labels truncate, so the bar can never overflow a 360px phone
 * even with long Ukrainian labels. Icons are stroked SVGs (decorative); the label is the accessible
 * name.
 */
export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: React.ReactNode;
  /** Match the index route exactly (so "/" doesn't stay active on child routes). */
  readonly end?: boolean;
  /** Optional pending count rendered as a small badge over the icon (e.g. the Inbox tab). */
  readonly badge?: number;
  /** Accessible label announcing the badge count (the numeric badge itself is decorative). */
  readonly badgeLabel?: string;
}

export function BottomNav({ items }: { items: readonly NavItem[] }): React.ReactElement {
  return (
    <nav className="flex items-center justify-around border-t border-border bg-surface px-2 pb-[calc(0.625rem+env(safe-area-inset-bottom,0px))] pt-2.5 shadow-nav sm:px-3">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className="flex min-h-11 min-w-0 max-w-28 flex-1 flex-col items-center gap-1 rounded-md px-1 py-1 text-xs font-semibold text-text-muted outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface sm:px-3"
        >
          {({ isActive }) => (
            <>
              <span
                className={cn(
                  'relative flex h-8 w-10 items-center justify-center rounded-md transition-colors',
                  isActive ? 'bg-primary text-primary-fg' : 'text-text-muted',
                )}
                aria-hidden="true"
              >
                {item.icon}
                {item.badge ? (
                  <span className="absolute -end-1 -top-1 inline-flex min-w-[1.125rem] items-center justify-center rounded-pill bg-danger px-1 text-[0.625rem] font-bold leading-none tabular-nums text-danger-fg">
                    {item.badge}
                  </span>
                ) : null}
              </span>
              <span className={cn('max-w-full truncate', isActive && 'text-text')}>
                {item.label}
              </span>
              {item.badge && item.badgeLabel ? (
                <span className="sr-only">{item.badgeLabel}</span>
              ) : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
